import { useEffect, useMemo, useState } from "react";

import type cbType from "@chargebee/chargebee-js-types";
import type {
  AdditionalData,
  PaymentIntent,
} from "@chargebee/chargebee-js-types/cb-types/internal/payment-intent/types";
import type CbInstance from "@chargebee/chargebee-js-types/cb-types/models/cb-instance";
import { LogSentryException, LogSentryMessage } from "@shapr3d/frontend-lib";

import type { CreditCardInputValue } from "../components/CreditCardInput/CreditCardInputTypes";
import { chargebeePublishableKey, chargebeeSiteName } from "../config";
import { isDevelopmentOrStageEnvironment } from "../config";
import { LoadScript } from "./LoadScript";

export const APPLE_PAY_NOT_SUPPORTED = "APPLE_PAY_NOT_SUPPORTED";
export const ApplePayButtonId = "apple-pay-button";

export const ChargebeePaymentType = {
  Card: "card",
  ApplePay: "apple_pay",
} as const;
export type ChargebeePaymentType = (typeof ChargebeePaymentType)[keyof typeof ChargebeePaymentType];
export const PaymentTypes = [
  ...Object.values(ChargebeePaymentType),
] satisfies ReadonlyArray<ChargebeePaymentType> as [
  ChargebeePaymentType,
  ...ChargebeePaymentType[],
];

declare const Chargebee: cbType;

interface ApplePayHandler {
  /**
   * General helper for deciding if the current environment supports Apple Pay or not.
   *
   * **WARNING: DO NOT TRUST IT BLINDLY** - sometimes it returns incorrect results.
   * Always have fallback handling in place.
   */
  canMakePayments: () => boolean;

  /**
   * Sets the payment intent for the Apple Pay session.
   *
   * Must be called before `mountPaymentButton`. To change the payment intent,
   * you need to remove the button from the DOM, call this method again with
   * the new payment intent, and then re-mount the button.
   */
  setPaymentIntent: (paymentIntent: PaymentIntent) => Promise<void>;

  /**
   * Mounts the Apple Pay button to the specified DOM selector.
   *
   * @param {string} selector - CSS selector for the element where the button should be mounted
   * @throws {Error} Throws `APPLE_PAY_NOT_SUPPORTED` error if Apple Pay is not supported
   *
   * @remarks
   * The options parameter is a mixed bag, typed from an API example.
   * The exact properties it accepts are not fully documented.
   * Reference: https://www.chargebee.com/checkout-portal-docs/payment-method-helper-common-api-ref.html#example-18
   */
  mountPaymentButton: (
    selector: string,
    options?: {
      locale?: string;
      buttonColor?: string;
      buttonType?: string;
    }
  ) => Promise<void>;

  /**
   * Handles the Apple Pay payment process.
   *
   * Should only be called after `mountPaymentButton` has been successfully executed.
   * The returned promise does not resolve until the user explicitly interacts with
   * the Apple Pay button or cancels the payment flow.
   */
  handlePayment: (callbacks?: {
    success?: (response: {
      paymentIntent: PaymentIntent;
      shippingAddress?: unknown;
      billingAddress?: unknown;
    }) => void;
    error?: (param: unknown) => void;
    change?: (param: unknown) => void;
  }) => Promise<void>;
}

interface ChargebeeErrorDetail {
  request_id?: string;
  error_category?: string;
  error_code?: string;
  error_message?: string;
  decline_code?: string;
  object?: string;
}

interface ChargebeeError extends Error {
  detail: ChargebeeErrorDetail;
  code: string;
  name: string;
  type: string;
  message: string;
  displayMessage: string;
}

export const useChargebee = () => {
  const [loaders, setLoaders] = useState<Readonly<{ [key in "chargebee" | "applePay"]: boolean }>>({
    chargebee: true,
    applePay: true,
  });
  const [isApplePaySupported, setIsApplePaySupported] = useState(false);

  useEffect(() => {
    ChargebeeAdapter.getInstance()
      .load()
      .then(() => setLoaders((loaders) => ({ ...loaders, chargebee: false })));
  }, []);

  useEffect(() => {
    const instance = ChargebeeAdapter.getInstance();

    if (!loaders.chargebee) {
      setIsApplePaySupported(instance.applePayHandler?.canMakePayments() ?? false);
      setLoaders((loaders) => ({ ...loaders, applePay: false }));
    }
  }, [loaders.chargebee]);

  const isLoading = useMemo(() => {
    return Object.values(loaders).some(Boolean);
  }, [loaders]);

  return { chargebee: ChargebeeAdapter.getInstance(), isLoading, isApplePaySupported };
};

export default class ChargebeeAdapter {
  private static instance: ChargebeeAdapter = new ChargebeeAdapter();

  static getInstance(): ChargebeeAdapter {
    return this.instance;
  }

  async validateVAT({ countryCode, VATNumber }: { countryCode: string; VATNumber: string }) {
    return this.chargebeeInstance?.vat.validateVat({
      country: countryCode,
      vat_number: VATNumber,
    });
  }

  private loadPromise: Promise<void> | undefined;
  public chargebeeInstance: CbInstance | undefined;
  public applePayHandler: ApplePayHandler | undefined;

  async load(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise((resolve, reject) => {
      LoadScript({ url: "https://js.chargebee.com/v2/chargebee.js", retry: 3 })
        .then(() => {
          Chargebee.init({
            site: chargebeeSiteName,
            publishableKey: chargebeePublishableKey,
          });
          this.chargebeeInstance = Chargebee.getInstance();
          this.chargebeeInstance
            .load("functions")
            .then(() => {
              if (window.location.protocol === "https:") {
                return this.chargebeeInstance?.load("apple-pay");
              }
            })
            .then((applePayHandler) => {
              this.applePayHandler = applePayHandler as ApplePayHandler | undefined;
              resolve();
            });
        })
        .catch((error) => {
          LogSentryException({ messagePrefix: "Chargebee error", error, level: "info" });
          reject(error);
          this.loadPromise = undefined;
        });
    });

    return this.loadPromise;
  }
}

type AuthorizePaymentIntentInput = {
  paymentType: ChargebeePaymentType | undefined | null;
  paymentIntent: PaymentIntent;
  card?: CreditCardInputValue;
  simulatedCreditCardError?: string;
};

export const authorizePaymentIntent = async ({
  paymentType,
  paymentIntent,
  card,
  simulatedCreditCardError,
}: AuthorizePaymentIntentInput) => {
  if (!paymentType) {
    return Promise.reject(new Error("Payment type is not defined"));
  }

  const additionalData: AdditionalData = {};

  if (
    paymentType === ChargebeePaymentType.Card &&
    isDevelopmentOrStageEnvironment &&
    simulatedCreditCardError
  ) {
    additionalData.billingAddress = { firstName: simulatedCreditCardError };
  }

  switch (paymentType) {
    case ChargebeePaymentType.ApplePay: {
      const applePayHandler = ChargebeeAdapter.getInstance().applePayHandler;

      if (!applePayHandler?.canMakePayments()) {
        LogSentryException({
          messagePrefix: "Apple Pay not supported",
          error: new Error('Invalid Apple Pay environment: "canMakePayments" returned false'),
          level: "error",
        });
        throw new Error(APPLE_PAY_NOT_SUPPORTED);
      }

      await applePayHandler?.setPaymentIntent(paymentIntent);

      await applePayHandler?.mountPaymentButton(`#${ApplePayButtonId}`);

      return new Promise<PaymentIntent>((resolve, reject) => {
        applePayHandler?.handlePayment({
          success: ({ paymentIntent }) => {
            LogSentryMessage({
              message: "Chargebee Apple Pay authentication success",
              level: "info",
            });
            resolve(paymentIntent);
          },
          error(param) {
            LogSentryException({
              messagePrefix: "Chargebee Apple Pay error",
              error: new Error(`Apple Pay error: ${JSON.stringify(param)}`),
              level: "info",
            });
            reject(param);
          },
          change(param) {
            LogSentryMessage({
              message: "Chargebee Apple Pay authentication change",
              level: "info",
              extra: { param },
            });
          },
        });
      });
    }

    case ChargebeePaymentType.Card: {
      if (!card) {
        return Promise.reject(new Error("Card is null"));
      }

      return card.API.authorizeWith3ds(paymentIntent, additionalData, {
        success: () => {
          LogSentryMessage({
            message: "Chargebee 3Ds authentication success",
            level: "info",
          });
        },
        change: () => {
          LogSentryMessage({
            message: "Chargebee 3Ds authentication change",
            level: "info",
          });
        },
        error: (paymentIntent: object, error: ChargebeeError) => {
          if (error.detail.error_category) {
            if (error.detail.error_category === "card_error") {
              LogSentryMessage({
                message: "Chargebee card error",
                level: "info",
                extra: {
                  chargebeeErrorCategory: error.detail.error_category,
                  chargebeeErrorCode: error.detail.error_code,
                  declineCode: error.detail.decline_code,
                  object: error.detail.object,
                },
              });
            } else {
              LogSentryException({
                messagePrefix: "Chargebee gateway integration error",
                error,
                extra: {
                  chargebeeErrorCategory: error.detail.error_category,
                  chargebeeErrorCode: error.detail.error_code,
                  declineCode: error.detail.decline_code,
                  object: error.detail.object,
                },
              });
            }
            return;
          }

          if (
            [
              "payment_intent_authentication_failure",
              "setup_intent_authentication_failure",
            ].includes(error.name)
          ) {
            const err = new Error(`3Ds authentication failure: ${error.name}`, { cause: error });
            LogSentryException({
              level: "info",
              error: err,
            });
            return;
          }

          LogSentryException({
            messagePrefix: "Chargebee error",
            error,
            level: "info",
          });
        },
      });
    }

    default: {
      const exhaustiveCheck: never = paymentType;
      return Promise.reject(new Error("Unknown payment type: " + exhaustiveCheck));
    }
  }
};
